/*
 * pp-article.js — JS del template del artículo en V3.
 *
 * Maneja:
 *   1. TOC auto-generado desde headings del post + scroll spy
 *   2. Text size selector (chico/medio/grande) con localStorage
 *   3. Audio player (play/pause, seek, progress)
 *   4. FAQ tabs + accordion
 *   5. Productos recomendados: add to cart sin recargar
 *
 * Toda manipulacion DOM usa createElement + textContent (no innerHTML
 * con strings construidos) para evitar XSS.
 */

(function () {
  'use strict';

  // ─────────────────────────────────────────────────────────────
  // 1. TOC + scroll spy
  // ─────────────────────────────────────────────────────────────
  function initToc() {
    const OFFSET = 24;
    const LOCK_MS = 800;

    const content = document.querySelector('#pp-post-body');
    const tocList = document.querySelector('#pp-toc-list');
    if (!content || !tocList) return;

    const headings = Array.from(content.querySelectorAll('h2, h3'));
    headings.forEach((h, i) => {
      if (!h.id) h.id = 'pp-section-' + (i + 1);
    });

    let currentH2Li = null;
    const mapIdToLink = new Map();

    // Limpiar lista sin innerHTML (seguro)
    while (tocList.firstChild) tocList.removeChild(tocList.firstChild);

    headings.forEach((h) => {
      if (h.tagName === 'H2') {
        currentH2Li = document.createElement('li');
        currentH2Li.className = 'pp-toc-h2 is-collapsed';

        const a = document.createElement('a');
        a.href = '#' + h.id;
        a.textContent = h.textContent.trim();
        currentH2Li.appendChild(a);

        const sub = document.createElement('ul');
        sub.className = 'pp-toc__sub';
        currentH2Li.appendChild(sub);

        tocList.appendChild(currentH2Li);
        mapIdToLink.set(h.id, a);
      } else if (h.tagName === 'H3' && currentH2Li) {
        const sub = currentH2Li.querySelector('.pp-toc__sub');
        const li = document.createElement('li');
        li.className = 'pp-toc-h3';
        const a = document.createElement('a');
        a.href = '#' + h.id;
        a.textContent = h.textContent.trim();
        li.appendChild(a);
        sub.appendChild(li);
        mapIdToLink.set(h.id, a);
        currentH2Li.classList.add('has-children');
      }
    });

    function openGroup(li) {
      if (li && li.classList.contains('has-children')) {
        li.classList.add('is-open');
        li.classList.remove('is-collapsed');
      }
    }

    function closeGroup(li) {
      if (li && li.classList.contains('has-children')) {
        li.classList.remove('is-open');
        li.classList.add('is-collapsed');
      }
    }

    function setActiveLink(id) {
      mapIdToLink.forEach((a) => a.classList.remove('is-active'));
      const a = mapIdToLink.get(id);
      if (a) a.classList.add('is-active');
      const el = document.getElementById(id);
      if (el && el.tagName === 'H3') {
        let prevH2 = null;
        for (let i = headings.indexOf(el); i >= 0; i--) {
          if (headings[i].tagName === 'H2') {
            prevH2 = headings[i];
            break;
          }
        }
        if (prevH2) {
          const link = mapIdToLink.get(prevH2.id);
          openGroup(link ? link.parentElement : null);
        }
      }
    }

    let lockUntil = 0;
    let lockId = null;

    tocList.addEventListener('click', function (e) {
      const a = e.target.closest('a');
      if (!a) return;
      const id = a.getAttribute('href').slice(1);
      const target = document.getElementById(id);
      if (!target) return;
      e.preventDefault();

      const li = a.parentElement;
      if (li.classList.contains('pp-toc-h2') && li.classList.contains('has-children')) {
        if (li.classList.contains('is-open')) closeGroup(li);
        else openGroup(li);
      }

      const y = target.getBoundingClientRect().top + window.scrollY - OFFSET;
      window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
      history.replaceState(null, '', '#' + id);

      lockId = id;
      lockUntil = Date.now() + LOCK_MS;
      setActiveLink(id);
    });

    const h2s = headings.filter((h) => h.tagName === 'H2');
    const h3s = headings.filter((h) => h.tagName === 'H3');

    function onScroll() {
      if (Date.now() < lockUntil && lockId) {
        setActiveLink(lockId);
        return;
      }

      // Pivot al ~30% del viewport: la sección se marca activa cuando su heading
      // entra al tercio superior, no cuando ya scrolleó off-screen
      const pivot = window.scrollY + Math.max(120, window.innerHeight * 0.3);

      let curH2 = null;
      h2s.forEach((h) => {
        if (h.offsetTop <= pivot) curH2 = h;
      });

      if (curH2) {
        setActiveLink(curH2.id);

        const idx = h2s.indexOf(curH2);
        const nextTop = idx + 1 < h2s.length ? h2s[idx + 1].offsetTop : Infinity;

        let curH3 = null;
        h3s.forEach((h) => {
          const t = h.offsetTop;
          if (t > curH2.offsetTop && t < nextTop && t <= pivot) curH3 = h;
        });
        if (curH3) setActiveLink(curH3.id);

        tocList.querySelectorAll('li.pp-toc-h2.has-children').forEach((li) => {
          const id = li.querySelector('a').getAttribute('href').slice(1);
          if (id === curH2.id) openGroup(li);
          else closeGroup(li);
        });
      }
    }

    if (location.hash) {
      const id = location.hash.slice(1);
      setActiveLink(id);
      const el = document.getElementById(id);
      if (el) {
        const y = el.getBoundingClientRect().top + window.scrollY - OFFSET;
        window.scrollTo({ top: Math.max(0, y), behavior: 'auto' });
      }
    }

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
  }

  // ─────────────────────────────────────────────────────────────
  // 2. Text size selector
  // ─────────────────────────────────────────────────────────────
  function initTextSize() {
    const KEY = 'pp_text_scale_v1';
    const ctr = document.querySelector('.pp-textsize');
    if (!ctr) return;
    const btns = ctr.querySelectorAll('.pp-ts-btn');
    const target = document.getElementById('pp-post-body');
    if (!target) return;

    function apply(scale) {
      document.documentElement.setAttribute('data-pp-ts', String(scale));
      target.style.setProperty('--ts-scale', scale);
      try {
        localStorage.setItem(KEY, scale);
      } catch (e) {}
      btns.forEach((b) => {
        const active = b.dataset.scale === String(scale);
        b.classList.toggle('is-active', active);
        b.setAttribute('aria-pressed', active);
      });
    }

    let stored = '1';
    try {
      stored = localStorage.getItem(KEY) || '1';
    } catch (e) {}
    apply(stored);

    ctr.addEventListener('click', function (e) {
      const btn = e.target.closest('.pp-ts-btn');
      if (!btn) return;
      apply(btn.dataset.scale);
    });
  }

  // ─────────────────────────────────────────────────────────────
  // 3. Audio player
  // ─────────────────────────────────────────────────────────────
  function initAudio() {
    function formatTime(t) {
      if (!isFinite(t)) return '0:00';
      const m = Math.floor(t / 60);
      const s = Math.floor(t % 60).toString().padStart(2, '0');
      return m + ':' + s;
    }

    document.querySelectorAll('.pp-audio').forEach(function (el) {
      const audio = el.querySelector('audio');
      const btn = el.querySelector('.pp-audio__btn');
      const bar = el.querySelector('.pp-audio__progress');
      const fill = el.querySelector('.pp-audio__progress-fill');
      const cur = el.querySelector('.pp-audio__current');
      const dur = el.querySelector('.pp-audio__duration');
      if (!audio || !btn || !bar) return;

      audio.addEventListener('loadedmetadata', function () {
        dur.textContent = formatTime(audio.duration);
      });

      function updateBtn() {
        if (audio.paused) {
          btn.classList.remove('is-playing');
          btn.textContent = '▶';
          btn.setAttribute('aria-label', 'Reproducir');
        } else {
          btn.classList.add('is-playing');
          btn.textContent = '❚❚';
          btn.setAttribute('aria-label', 'Pausar');
        }
      }

      btn.addEventListener('click', function () {
        if (audio.paused) audio.play();
        else audio.pause();
      });
      audio.addEventListener('play', updateBtn);
      audio.addEventListener('pause', updateBtn);

      function setProgress() {
        const pct = audio.duration ? (audio.currentTime / audio.duration) * 100 : 0;
        fill.style.width = pct + '%';
        cur.textContent = formatTime(audio.currentTime);
        bar.setAttribute('aria-valuenow', Math.round(pct));
      }
      audio.addEventListener('timeupdate', setProgress);

      function seekFromEvent(e) {
        const rect = bar.getBoundingClientRect();
        const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
        let pct = (clientX - rect.left) / rect.width;
        pct = Math.max(0, Math.min(1, pct));
        if (audio.duration) audio.currentTime = pct * audio.duration;
      }
      bar.addEventListener('click', seekFromEvent, { passive: true });
      bar.addEventListener('touchstart', seekFromEvent, { passive: true });

      bar.addEventListener('keydown', function (e) {
        if (!audio.duration) return;
        const step = audio.duration * 0.05;
        if (e.key === 'ArrowRight') {
          audio.currentTime = Math.min(audio.duration, audio.currentTime + step);
          e.preventDefault();
        }
        if (e.key === 'ArrowLeft') {
          audio.currentTime = Math.max(0, audio.currentTime - step);
          e.preventDefault();
        }
        if (e.key === 'Home') {
          audio.currentTime = 0;
          e.preventDefault();
        }
        if (e.key === 'End') {
          audio.currentTime = audio.duration;
          e.preventDefault();
        }
      });

      updateBtn();
      setProgress();
    });
  }

  // ─────────────────────────────────────────────────────────────
  // 4. FAQ tabs + accordion
  // ─────────────────────────────────────────────────────────────
  function initFaq() {
    const root = document.querySelector('[data-pp-faq]');
    if (!root) return;

    const tabs = Array.from(root.querySelectorAll('[data-pp-faq-tab]'));
    const panels = Array.from(root.querySelectorAll('[data-pp-faq-panel]'));

    function openTab(name) {
      tabs.forEach(function (t) {
        const on = t.getAttribute('data-pp-faq-tab') === name;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
      });
      panels.forEach(function (p) {
        const on = p.getAttribute('data-pp-faq-panel') === name;
        p.hidden = !on;
        p.classList.toggle('is-active', on);
      });
    }

    root.addEventListener(
      'click',
      function (e) {
        const tab = e.target.closest('[data-pp-faq-tab]');
        if (tab) {
          openTab(tab.getAttribute('data-pp-faq-tab'));
          return;
        }

        const btn = e.target.closest('[data-pp-acc-btn]');
        if (!btn) return;

        const item = btn.closest('.pp-faq__item');
        const panel = item ? item.querySelector('[data-pp-acc-panel]') : null;
        if (!panel) return;

        const isOpen = btn.getAttribute('aria-expanded') === 'true';
        const acc = btn.closest('[data-pp-acc]');

        if (acc) {
          acc.querySelectorAll('[data-pp-acc-btn]').forEach(function (b) {
            if (b !== btn) b.setAttribute('aria-expanded', 'false');
          });
          acc.querySelectorAll('[data-pp-acc-panel]').forEach(function (p) {
            if (p !== panel) p.hidden = true;
          });
        }

        btn.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
        panel.hidden = isOpen ? true : false;
      },
      { passive: true }
    );

    if (tabs.length > 0) {
      openTab(tabs[0].getAttribute('data-pp-faq-tab'));
    }
  }

  // ─────────────────────────────────────────────────────────────
  // 5. Productos recomendados: marcar in-cart + add to cart
  // ─────────────────────────────────────────────────────────────
  function markRecsInCart() {
    fetch('/cart.js')
      .then((res) => res.json())
      .then((cart) => {
        const ids = cart.items.map((i) => i.variant_id);
        document.querySelectorAll('.pp-recs__add').forEach((btn) => {
          const vid = Number(btn.getAttribute('data-variant-id'));
          if (ids.includes(vid)) {
            btn.classList.add('is-added');
            btn.textContent = '✓';
            const card = btn.closest('.pp-recs__card');
            if (card) card.remove();
          }
        });
      })
      .catch(() => {});
  }

  function initRecsAddToCart() {
    document.querySelectorAll('.pp-recs__add').forEach((btn) => {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        if (btn.classList.contains('is-added')) return;

        const vid = btn.getAttribute('data-variant-id');
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: vid, quantity: 1 }),
        })
          .then((res) => {
            if (!res.ok) throw new Error('add failed');
            return res.json();
          })
          .then(() => {
            btn.textContent = '✓';
            btn.classList.add('is-added');
            const card = btn.closest('.pp-recs__card');
            if (card) {
              card.style.transition = 'opacity 0.2s ease';
              card.style.opacity = '0';
              setTimeout(() => {
                card.style.display = 'none';
              }, 200);
            }
            try {
              window.dispatchEvent(new CustomEvent('cart:updated'));
            } catch (e) {}
          })
          .catch(() => {
            alert('No pudimos agregar el producto. Intenta de nuevo.');
          });
      });
    });
  }

  // ─────────────────────────────────────────────────────────────
  // 6. Share button (Web Share API + dropdown fallback)
  // ─────────────────────────────────────────────────────────────
  function initShare() {
    const share = document.querySelector('[data-pp-share]');
    if (!share) return;

    const toggle = share.querySelector('[data-pp-share-toggle]');
    const nativeBtn = share.querySelector('[data-pp-share-native]');
    const copyBtn = share.querySelector('[data-pp-share-copy]');

    // Si hay Web Share API: el click del botón principal dispara
    // navigator.share directo (mobile-first UX). Sino, abre dropdown.
    const hasNativeShare = typeof navigator.share === 'function';

    if (!hasNativeShare && nativeBtn) {
      // Sin soporte: ocultar el item "Compartir…" del menu
      nativeBtn.style.display = 'none';
    }

    function closeMenu() {
      share.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
    }

    function openMenu() {
      share.classList.add('is-open');
      toggle.setAttribute('aria-expanded', 'true');
    }

    function triggerNativeShare(url, title) {
      navigator
        .share({ title: title, url: url })
        .catch(() => {});
    }

    function copyToClipboard(text) {
      const done = () => {
        share.classList.add('is-copied');
        setTimeout(() => share.classList.remove('is-copied'), 1800);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(done).catch(() => {
          // Fallback con execCommand
          const ta = document.createElement('textarea');
          ta.value = text;
          ta.style.position = 'fixed';
          ta.style.opacity = '0';
          document.body.appendChild(ta);
          ta.select();
          try { document.execCommand('copy'); done(); } catch (e) {}
          document.body.removeChild(ta);
        });
      }
    }

    toggle.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      if (hasNativeShare) {
        const url = nativeBtn ? nativeBtn.getAttribute('data-share-url') : window.location.href;
        const title = nativeBtn ? nativeBtn.getAttribute('data-share-title') : document.title;
        triggerNativeShare(url, title);
      } else {
        if (share.classList.contains('is-open')) closeMenu();
        else openMenu();
      }
    });

    if (nativeBtn && hasNativeShare) {
      nativeBtn.addEventListener('click', function (e) {
        e.preventDefault();
        const url = nativeBtn.getAttribute('data-share-url');
        const title = nativeBtn.getAttribute('data-share-title');
        triggerNativeShare(url, title);
        closeMenu();
      });
    }

    if (copyBtn) {
      copyBtn.addEventListener('click', function (e) {
        e.preventDefault();
        copyToClipboard(copyBtn.getAttribute('data-share-url'));
        closeMenu();
      });
    }

    // Cerrar menu al click afuera o ESC
    document.addEventListener('click', function (e) {
      if (!share.contains(e.target)) closeMenu();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeMenu();
    });
  }

  // ─────────────────────────────────────────────────────────────
  // Reading progress bar — barra fija que se llena con el scroll
  // ─────────────────────────────────────────────────────────────
  function initProgress() {
    const bar = document.querySelector('#pp-article-progress .pp-article__progress-fill');
    const body = document.querySelector('#pp-post-body');
    if (!bar || !body) return;

    let ticking = false;
    function update() {
      const rect = body.getBoundingClientRect();
      const bodyTop = rect.top + window.scrollY;
      const bodyHeight = body.offsetHeight;
      const viewportHeight = window.innerHeight;
      const scrolled = window.scrollY - bodyTop;
      const readable = bodyHeight - viewportHeight;

      let pct;
      if (readable <= 0) {
        pct = scrolled >= 0 ? 100 : 0;
      } else {
        pct = (scrolled / readable) * 100;
      }

      bar.style.width = Math.max(0, Math.min(100, pct)) + '%';
      ticking = false;
    }
    function onScroll() {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    }

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
  }

  // ─────────────────────────────────────────────────────────────
  // Disable header reveal en artículos — el header NO baja al scrollear up
  // ─────────────────────────────────────────────────────────────
  function disableHeaderReveal() {
    const stickyHeader = document.querySelector('sticky-header');
    if (!stickyHeader) return;
    const setPrevent = () => { stickyHeader.preventReveal = true; };
    setPrevent();
    window.addEventListener('scroll', setPrevent, { passive: true });
  }

  // ─────────────────────────────────────────────────────────────
  // Init
  // ─────────────────────────────────────────────────────────────
  function init() {
    initToc();
    initTextSize();
    initAudio();
    initFaq();
    markRecsInCart();
    initRecsAddToCart();
    initShare();
    initProgress();
    disableHeaderReveal();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
